<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else-if="!isError" class="customer-project-detail">
    <div v-if="project" :key="project.id" class="space-y-6">
      <!-- Project Header -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div class="flex-1">
            <div class="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <h1 class="text-2xl font-bold text-gray-900">{{ project.name }}</h1>
              <ProjectStatusBadge :status="project.status" />
            </div>
            
            <div v-if="project.type" class="mb-4">
              <ProjectTypeBadge :type="project.type" />
            </div>

            <div v-if="project.description" class="text-gray-600">
              <p>{{ project.description }}</p>
            </div>
          </div>

          <!-- Quick Actions -->
          <div class="flex flex-col sm:flex-row gap-2">
            <BaseButton
              v-if="projectProgress === 100"
              :to="{ name: 'ListInvoices', params: { projectId } }"
              variant="primary"
              icon-class="i-heroicons-document-text"
            >
              View Invoices
            </BaseButton>
            <BaseButton
              v-if="proposal?.id"
              :to="{ name: 'CustomerViewProposal', params: { id: proposal.id }}"
              variant="primary"
              icon-class="i-heroicons-document-text"
            >
              View Proposal
            </BaseButton>
            <BaseButton
              variant="accent"
              icon-class="i-heroicons-chat-bubble-left-right"
              @click="scrollToMessages"
            >
              Message Team
            </BaseButton>
          </div>
        </div>
      </div>

      <!-- Project Overview Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Project Progress -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Progress Section -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Project Progress</h2>
            
            <div class="space-y-4">
              <div>
                <div class="flex justify-between items-center mb-2">
                  <span class="text-sm font-medium text-gray-700">
                    Overall Progress
                    {{ completedItems.length }}/{{ budget.lineItems.length }}
                  </span>
                  <span class="text-sm font-semibold text-gray-900">{{ projectProgress }}%</span>
                </div>
                <BaseProgressBar :progress="projectProgress" variant="primary" />
              </div>

              <!-- Budget Progress -->
              <div class="space-y-3">
                <h3 class="text-sm font-medium text-gray-700">Task Progress</h3>
                <div class="space-y-2">
                  <div
                    v-for="item in budget.lineItems.slice(0, 5)"
                    :key="item.id"
                    class="flex items-center justify-between text-sm"
                  >
                    <span class="text-gray-600 truncate flex-1 mr-2">{{ item.title }}</span>
                    <BudgetItemStatusBadge :status="item.status" />
                  </div>
                  <div v-if="budget.lineItems.length > 5" class="text-xs text-gray-500 text-center">
                    +{{ budget.lineItems.length - 5 }} more items
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Project Timeline -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Project Timeline</h2>
            
            <div class="space-y-4">
              <div class="flex items-center gap-3">
                <div class="w-3 h-3 bg-primary rounded-full"></div>
                <div>
                  <div class="text-sm font-medium text-gray-900">Project Started</div>
                  <div class="text-xs text-gray-500">{{ formatDate(project.createdAt) }}</div>
                </div>
              </div>

              <div v-if="(project as any).estimatedStartDate" class="flex items-center gap-3">
                <div class="w-3 h-3 bg-accent rounded-full"></div>
                <div>
                  <div class="text-sm font-medium text-gray-900">Estimated Start</div>
                  <div class="text-xs text-gray-500">{{ formatDate((project as any).estimatedStartDate) }}</div>
                </div>
              </div>

              <div v-if="(project as any).estimatedEndDate" class="flex items-center gap-3">
                <div class="w-3 h-3 bg-warning rounded-full"></div>
                <div>
                  <div class="text-sm font-medium text-gray-900">Estimated Completion</div>
                  <div class="text-xs text-gray-500">{{ formatDate((project as any).estimatedEndDate) }}</div>
                </div>
              </div>

              <div v-if="(project as any).completedAt" class="flex items-center gap-3">
                <div class="w-3 h-3 bg-success rounded-full"></div>
                <div>
                  <div class="text-sm font-medium text-gray-900">Project Completed</div>
                  <div class="text-xs text-gray-500">{{ formatDate((project as any).completedAt) }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Communication Section -->
          <div id="communication-section" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Communication</h2>
            <CustomerProjectMessages :project="project" />
          </div>
        </div>

        <!-- Sidebar -->
        <div class="space-y-6">
          <!-- Project Details -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Project Details</h3>
            
            <div class="space-y-4">
              <div v-if="project.address">
                <div class="flex items-start gap-2">
                  <span class="i-heroicons-map-pin text-gray-400 mt-0.5"></span>
                  <div>
                    <div class="text-sm font-medium text-gray-700">Address</div>
                    <div class="text-sm text-gray-600">{{ project.address }}</div>
                  </div>
                </div>
              </div>

              <div v-if="(budget as any)?.totalAmount">
                <div class="flex items-start gap-2">
                  <span class="i-heroicons-currency-dollar text-gray-400 mt-0.5"></span>
                  <div>
                    <div class="text-sm font-medium text-gray-700">Project Value</div>
                    <div class="text-sm text-gray-600">${{ formatCurrency((budget as any).totalAmount) }}</div>
                  </div>
                </div>
              </div>

              <div v-if="project.type">
                <div class="flex items-start gap-2">
                  <span class="i-heroicons-tag text-gray-400 mt-0.5"></span>
                  <div>
                    <div class="text-sm font-medium text-gray-700">Project Type</div>
                    <div class="text-sm text-gray-600">{{ project.type }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Team Contact -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Your Team</h3>
            
            <div class="space-y-4">
              <div v-if="projectManager">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span class="i-heroicons-user text-primary"></span>
                  </div>
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ projectManager.name }}</div>
                    <div class="text-xs text-gray-500">Project Manager</div>
                  </div>
                </div>
              </div>

              <div v-if="salesPerson">
                <div class="flex items-start gap-2">
                  <div class="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span class="i-heroicons-user text-primary"></span>
                  </div>
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ salesPerson.name }}</div>
                    <div class="text-xs text-gray-500">Sales Person</div>
                  </div>
                </div>
              </div>

              <div v-if="realtor">
                <div class="flex items-start gap-2">
                  <div class="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span class="i-heroicons-user text-primary"></span>
                  </div>
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ realtor.name }}</div>
                    <div class="text-xs text-gray-500">Realtor</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <EmptyState v-else>
    <template #icon>
      <span class="i-heroicons-exclamation-circle text-red-500 size-8"></span>
    </template>
    <template #title>
      <h2 class="text-lg font-semibold text-center text-slate-900">Project Not Found</h2>
    </template>
    <template #description>
      <p class="text-sm text-center text-slate-500 pt-4">
        The project you're looking for could not be found or you don't have permission to view it.
      </p>
    </template>
  </EmptyState>
</template>

<script lang="ts" setup>
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import ProjectService from '@/services/ProjectService'
import CustomerService from '@/services/CustomerService'
import { computed } from 'vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import EmptyState from '@/components/app/EmptyState.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ProjectStatusBadge from '@/components/project/ProjectStatusBadge.vue'
import ProjectTypeBadge from '@/components/project/ProjectTypeBadge.vue'
import BaseProgressBar from '@/components/base/BaseProgressBar.vue'
import BudgetService from '@/services/BudgetService'
import ProposalService from '@/services/ProposalService'
import { BUDGET_ITEM_STATUS, type BudgetItem } from '@/models/budget'
import { eq, orderByDesc } from '@/utils/queryFilters'
import { displayDate } from '@/utils'
import CustomerProjectMessages from '@/components/project/CustomerProjectMessages.vue'
import UserService from '@/services/UserService'
import BudgetItemStatusBadge from '@/components/budget/BudgetItemStatusBadge.vue'

useHead({
  title: 'Project Details'
})

const $route = useRoute()
const projectId = $route.params.projectId as string
const customerId = $route.params.customerId as string

const projectService = new ProjectService()
const { data: project, isLoading: projectLoading, error: projectError } = useQuery({
  queryKey: ['project', projectId],
  queryFn: async () => {
    const project = await projectService.where(
      [eq('id', projectId), eq('customerId', customerId)],
      [orderByDesc('createdAt')],
      1
    )
    return project[0]
  },
  enabled: !!projectId && !!customerId
})

const { data: projectManager, isLoading: projectManagerLoading, error: projectManagerError } = useQuery({
  queryKey: ['user', project.value?.projectManagerId],
  queryFn: () => new UserService().find(project.value?.projectManagerId),
  enabled: computed(() => !!project.value?.projectManagerId)
})

const { data: salesPerson, isLoading: salesPersonLoading, error: salesPersonError } = useQuery({
  queryKey: ['user', project.value?.salesPersonId],
  queryFn: () => new UserService().find(project.value?.salesPersonId),
  enabled: computed(() => !!project.value?.salesPersonId)
})

const { data: realtor, isLoading: realtorLoading, error: realtorError } = useQuery({
  queryKey: ['customer', project.value?.realtorId],
  queryFn: () => new CustomerService().find(project.value?.realtorId),
  enabled: computed(() => !!project.value?.realtorId)
})

const { data: budget, isLoading: isBudgetLoading } = useQuery({
  queryKey: ['budgets', projectId],
  queryFn: async () => {
    const budget = await new BudgetService().find(projectId)
    return budget
  },
  enabled: !!projectId
})

const { data: proposal, isLoading: isProposalLoading } = useQuery({
  queryKey: ['proposals', projectId],
  queryFn: async () => {
    const proposals = await new ProposalService().where(
      [
        eq('projectId', projectId),
        eq('customerId', customerId)
      ],
      [orderByDesc('createdAt')],
      1
    )
    return proposals[0]
  },
  enabled: !!projectId && !!customerId
})

const isLoading = computed(() => 
  projectLoading.value || 
  projectManagerLoading.value || 
  salesPersonLoading.value || 
  isBudgetLoading.value || 
  isProposalLoading.value ||
  realtorLoading.value
)

const isError = computed(() => projectError.value || projectManagerError.value || salesPersonError.value || realtorError.value)

const completedItems = computed(() => {
  if (!budget.value?.lineItems?.length) return []

  return budget.value.lineItems.filter((item: BudgetItem) => 
    item.status === BUDGET_ITEM_STATUS.COMPLETED
  )
})

const projectProgress = computed(() => {
  if (!budget.value?.lineItems?.length) return 0

  return Math.round((completedItems.value.length / budget.value.lineItems.length) * 100)
})

const scrollToMessages = () => {
  // Scroll to the communication section
  const communicationSection = document.querySelector('#communication-section')
  if (communicationSection) {
    communicationSection.scrollIntoView({ behavior: 'smooth' })
  }
}

const formatDate = (date: string | Date) => {
  if (!date) return ''
  const dateObj = date instanceof Date ? date : new Date(date)
  return displayDate(dateObj.getTime() / 1000)
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}
</script>
